import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CloseIcon from '@mui/icons-material/Close';

import { modifierLabels } from './canvas-shortcuts';

/** A row of styled key caps, e.g. [Cmd][Opt][T]. */
function Keys({ keys }: { keys: string[] }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
      {keys.map((k, i) => (
        <Box
          key={`${k}-${i}`}
          component="span"
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            fontSize: 11,
            fontFamily: 'monospace',
            lineHeight: 1.4,
            minWidth: 16,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {k}
        </Box>
      ))}
    </Stack>
  );
}

interface ShortcutGroup {
  title: string;
  items: { label: string; keys: string[] }[];
}

/**
 * Floating "?" keyboard-shortcuts affordance, pinned to the top-right of the
 * canvas. Lists every binding handled in useEditorEvents.ts, grouped for
 * scanning. Shown wherever CanvasKitEditor renders, so the reference travels
 * with the editor (email builder, workspace, social ads).
 */
export function CanvasShortcutsHelp() {
  const { t } = useTranslation('canvas');
  const [open, setOpen] = useState(false);
  const m = modifierLabels();
  const arrows = t('shortcuts.arrows', 'Arrows');
  const drag = t('shortcuts.drag', 'Drag');
  const click = t('shortcuts.click', 'Click');
  const hover = t('shortcuts.hover', 'Hover');

  const groups: ShortcutGroup[] = [
    {
      title: t('shortcuts.groups.essentials', 'Essentials'),
      items: [
        { label: t('shortcuts.undo', 'Undo'), keys: [m.cmd, 'Z'] },
        { label: t('shortcuts.redo', 'Redo'), keys: [m.cmd, m.shift, 'Z'] },
        { label: t('shortcuts.redoAlt', 'Redo (alternate)'), keys: [m.cmd, 'Y'] },
        { label: t('shortcuts.copy', 'Copy'), keys: [m.cmd, 'C'] },
        { label: t('shortcuts.cut', 'Cut'), keys: [m.cmd, 'X'] },
        { label: t('shortcuts.paste', 'Paste at cursor'), keys: [m.cmd, 'V'] },
        { label: t('shortcuts.duplicate', 'Duplicate'), keys: [m.cmd, 'D'] },
        { label: t('shortcuts.delete', 'Delete'), keys: ['Del'] },
        { label: t('shortcuts.rename', 'Rename layer'), keys: [m.cmd, 'R'] },
      ],
    },
    {
      title: t('shortcuts.groups.tools', 'Tools'),
      items: [
        { label: t('shortcuts.toolSelect', 'Select / move'), keys: ['V'] },
        { label: t('shortcuts.toolRectangle', 'Rectangle'), keys: ['R'] },
        { label: t('shortcuts.toolEllipse', 'Ellipse'), keys: ['O'] },
        { label: t('shortcuts.toolLine', 'Line'), keys: ['L'] },
        { label: t('shortcuts.toolPen', 'Pen'), keys: ['P'] },
        { label: t('shortcuts.toolPencil', 'Pencil'), keys: [m.shift, 'P'] },
        { label: t('shortcuts.toolText', 'Add text'), keys: ['T'] },
      ],
    },
    {
      title: t('shortcuts.groups.panels', 'Panels'),
      items: [
        { label: t('shortcuts.panelLayers', 'Layers (1st tab)'), keys: [m.alt, '1'] },
        { label: t('shortcuts.panelTabs', 'Sidebar tabs, in icon order'), keys: [m.alt, '1', '–', '9'] },
        { label: t('shortcuts.panelToggle', 'Reopen / close tab'), keys: [m.alt, t('shortcuts.sameNumber', 'same number')] },
        { label: t('shortcuts.panelProperties', 'Properties'), keys: [m.alt, 'P'] },
        { label: t('shortcuts.panelChat', 'Chat / Remix'), keys: [m.alt, 'C'] },
      ],
    },
    {
      title: t('shortcuts.groups.fillStroke', 'Fill & stroke'),
      items: [
        { label: t('shortcuts.removeStroke', 'Remove stroke'), keys: ['/'] },
        { label: t('shortcuts.removeFill', 'Remove fill'), keys: [m.alt, '/'] },
        { label: t('shortcuts.swapFillStroke', 'Swap fill & stroke'), keys: [m.shift, 'X'] },
        { label: t('shortcuts.flatten', 'Flatten two shapes'), keys: [m.cmd, 'E'] },
        { label: t('shortcuts.outlineText', 'Outline text'), keys: [m.cmd, m.shift, 'O'] },
      ],
    },
    {
      title: t('shortcuts.groups.select', 'Select'),
      items: [
        { label: t('shortcuts.selectAll', 'Select all'), keys: [m.cmd, 'A'] },
        { label: t('shortcuts.deepSelect', 'Deep select'), keys: [m.cmd, click] },
        { label: t('shortcuts.addToSelection', 'Add to selection'), keys: [m.shift, click] },
        { label: t('shortcuts.enterGroup', 'Enter group'), keys: ['Enter'] },
        { label: t('shortcuts.exitGroup', 'Select parent'), keys: [m.shift, 'Enter'] },
        { label: t('shortcuts.nextSibling', 'Next / prev sibling'), keys: ['Tab'] },
      ],
    },
    {
      title: t('shortcuts.groups.transform', 'Move & transform'),
      items: [
        { label: t('shortcuts.dupDrag', 'Duplicate-drag'), keys: [m.alt, drag] },
        { label: t('shortcuts.axisLock', 'Lock to axis'), keys: [m.shift, drag] },
        { label: t('shortcuts.resizeCenter', 'Resize from center'), keys: [m.alt, drag] },
        { label: t('shortcuts.aspectLock', 'Keep aspect ratio'), keys: [m.shift, drag] },
        { label: t('shortcuts.disableSnap', 'Disable snapping'), keys: [m.cmd, drag] },
        { label: t('shortcuts.measure', 'Measure to target'), keys: [m.alt, hover] },
        { label: t('shortcuts.copyStyle', 'Copy style'), keys: [m.cmd, m.alt, 'C'] },
        { label: t('shortcuts.pasteStyle', 'Paste style'), keys: [m.cmd, m.alt, 'V'] },
        { label: t('shortcuts.nudge', 'Move 1px'), keys: [arrows] },
        { label: t('shortcuts.nudgeBig', 'Move 10px'), keys: [m.shift, arrows] },
      ],
    },
    {
      title: t('shortcuts.groups.arrange', 'Arrange'),
      items: [
        { label: t('shortcuts.group', 'Group'), keys: [m.cmd, 'G'] },
        { label: t('shortcuts.ungroup', 'Ungroup'), keys: [m.cmd, m.shift, 'G'] },
        { label: t('shortcuts.frame', 'Frame selection'), keys: [m.cmd, m.alt, 'G'] },
        { label: t('shortcuts.forward', 'Bring forward'), keys: [m.cmd, ']'] },
        { label: t('shortcuts.backward', 'Send backward'), keys: [m.cmd, '['] },
        { label: t('shortcuts.toFront', 'To front'), keys: [m.cmd, m.alt, ']'] },
        { label: t('shortcuts.toBack', 'To back'), keys: [m.cmd, m.alt, '['] },
        { label: t('shortcuts.lock', 'Lock'), keys: [m.cmd, m.shift, 'L'] },
        { label: t('shortcuts.hide', 'Hide'), keys: [m.cmd, m.shift, 'H'] },
      ],
    },
    {
      title: t('shortcuts.groups.alignLayout', 'Align & layout'),
      items: [
        { label: t('shortcuts.objAlignLeft', 'Align left'), keys: [m.alt, 'A'] },
        { label: t('shortcuts.objAlignRight', 'Align right'), keys: [m.alt, 'D'] },
        { label: t('shortcuts.objAlignTop', 'Align top'), keys: [m.alt, 'W'] },
        { label: t('shortcuts.objAlignBottom', 'Align bottom'), keys: [m.alt, 'S'] },
        { label: t('shortcuts.objAlignHCenter', 'Center horizontally'), keys: [m.alt, 'H'] },
        { label: t('shortcuts.objAlignVCenter', 'Center vertically'), keys: [m.alt, 'V'] },
        { label: t('shortcuts.distributeH', 'Distribute horizontally'), keys: ['Ctrl', m.alt, 'H'] },
        { label: t('shortcuts.distributeV', 'Distribute vertically'), keys: ['Ctrl', m.alt, 'V'] },
        { label: t('shortcuts.tidyUp', 'Tidy up'), keys: ['Ctrl', m.alt, 'T'] },
        { label: t('shortcuts.autoLayoutOn', 'Add auto-layout'), keys: [m.shift, 'A'] },
        { label: t('shortcuts.autoLayoutOff', 'Remove auto-layout'), keys: [m.alt, m.shift, 'A'] },
        { label: t('shortcuts.float', 'Float / dock in layout'), keys: [m.shift, 'F'] },
      ],
    },
    {
      title: t('shortcuts.groups.text', 'Text'),
      items: [
        { label: t('shortcuts.bold', 'Bold'), keys: [m.cmd, 'B'] },
        { label: t('shortcuts.italic', 'Italic'), keys: [m.cmd, 'I'] },
        { label: t('shortcuts.underline', 'Underline'), keys: [m.cmd, 'U'] },
        { label: t('shortcuts.strikethrough', 'Strikethrough'), keys: [m.cmd, m.shift, 'X'] },
        { label: t('shortcuts.fontLarger', 'Larger'), keys: [m.cmd, m.shift, '>'] },
        { label: t('shortcuts.fontSmaller', 'Smaller'), keys: [m.cmd, m.shift, '<'] },
        { label: t('shortcuts.weightUp', 'Weight heavier'), keys: [m.cmd, m.alt, '>'] },
        { label: t('shortcuts.weightDown', 'Weight lighter'), keys: [m.cmd, m.alt, '<'] },
        { label: t('shortcuts.letterSpacing', 'Letter spacing'), keys: [m.alt, '< >'] },
        { label: t('shortcuts.lineHeight', 'Line height'), keys: [m.shift, m.alt, '< >'] },
        { label: t('shortcuts.alignLeft', 'Align left'), keys: [m.cmd, m.alt, 'L'] },
        { label: t('shortcuts.alignCenter', 'Align center'), keys: [m.cmd, m.alt, 'T'] },
        { label: t('shortcuts.alignRight', 'Align right'), keys: [m.cmd, m.alt, 'R'] },
        { label: t('shortcuts.alignJustify', 'Justify'), keys: [m.cmd, m.alt, 'J'] },
      ],
    },
    {
      title: t('shortcuts.groups.view', 'View'),
      items: [
        { label: t('shortcuts.zoomIn', 'Zoom in'), keys: [m.cmd, '='] },
        { label: t('shortcuts.zoomOut', 'Zoom out'), keys: [m.cmd, '-'] },
        { label: t('shortcuts.zoom100', 'Zoom to 100%'), keys: [m.cmd, '0'] },
        { label: t('shortcuts.zoomFit', 'Zoom to fit'), keys: [m.shift, '1'] },
        { label: t('shortcuts.zoomSelection', 'Zoom to selection'), keys: [m.shift, '2'] },
        { label: t('shortcuts.toggleGuides', 'Show / hide guides'), keys: [m.shift, 'G'] },
        { label: t('shortcuts.opacity', 'Set opacity'), keys: ['0', '–', '9'] },
        { label: t('shortcuts.pan', 'Pan canvas'), keys: ['Space', drag] },
        { label: t('shortcuts.deselect', 'Deselect / exit'), keys: ['Esc'] },
      ],
    },
  ];

  return (
    <>
      <Tooltip title={t('shortcuts.tooltip', 'Keyboard shortcuts')} placement="left">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label={t('shortcuts.tooltip', 'Keyboard shortcuts')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 6,
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <KeyboardIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {t('shortcuts.title', 'Keyboard shortcuts')}
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 4,
              rowGap: 3,
            }}
          >
            {groups.map((g) => (
              <Box key={g.title}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '10px',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {g.title}
                </Typography>
                <Stack spacing={1}>
                  {g.items.map((it) => (
                    <Box
                      key={it.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2">{it.label}</Typography>
                      <Keys keys={it.keys} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
